import { useParams } from 'react-router-dom'
import { useContext, useState, useEffect } from 'react'
import { APIContext } from 'context/APIContext'
import { useGetExercises } from 'redux/customHooks/useGetExercises'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { initializeActiveWorkoutV5 } from 'redux/slices/activeWorkoutV5'

import ScreenFrame from 'components/Frame/ScreenFrame'
import SolidButton from 'components/Buttons/SolidButton'
import BrandColorButton from 'components/Buttons/BrandColorButton'
import GroupAttendance from 'components/ActiveWorkoutPreview/GroupAttendance'
import UserProfilePicture from 'components/User/UserProfilePicture'
import WorkoutV5EquipmentPreview from 'components/ActiveWorkoutPreview/WorkoutV5EquipmentPreview'
import WorkoutV5PreviewComponent from 'components/ActiveWorkoutPreview/WorkoutV5PreviewComponent'

export default function WorkoutV5PreviewView() {

    const dispatch = useDispatch()
    const navigate = useNavigate()

    const { urlWorkoutId } = useParams()

    const { exercises, exerciseHashMap } = useGetExercises()

    const { GetWorkoutV5Preview, StartV5Workouts } = useContext(APIContext)

    const coachUserId = useSelector((state) => state.user.id)

    const [workoutPreviewData, setWorkoutPreviewData] = useState(null)
    const [startWorkoutLoading, setStartWorkoutLoading] = useState(false)

    const kinesisWorkoutAssignmentType = workoutPreviewData?.kinesisWorkoutAssignmentType

    // A group individual's copy of a group workout (opened from the athlete's own profile) runs as an individual workout, like the athlete app

    const isIndividualRun = kinesisWorkoutAssignmentType === 'individual' || kinesisWorkoutAssignmentType === 'group-individual'
    const groupDetails = workoutPreviewData?.groupDetails
    const userDetails = workoutPreviewData?.userDetails
    const workout = workoutPreviewData?.workout
    const groupIndividualData = workoutPreviewData?.groupIndividualData

    const LoadWorkoutPreview = async () => {
        setWorkoutPreviewData(null)
        const newWorkoutPreviewData = await GetWorkoutV5Preview(urlWorkoutId)
        setWorkoutPreviewData(newWorkoutPreviewData)
    }

    const SetGroupIndividualDataInsideWorkoutPreviewDataObject = (newGroupIndividualData) => {
        setWorkoutPreviewData((prevWorkoutPreviewData) => ({
            ...prevWorkoutPreviewData,
            groupIndividualData: newGroupIndividualData
        }))
    }

    const OnStartWorkoutButton = async () => {
        if (kinesisWorkoutAssignmentType === 'group') {
            await __StartGroupWorkout()
        }

        if (isIndividualRun) {
            await __StartIndividualWorkout()
        }
    }

    const __StartGroupWorkout = async () => {

        if (urlWorkoutId !== workout.workoutId) {
            console.error('Workout ID mismatch!')
            return
        }

        setStartWorkoutLoading(true)

        // Get present group individuals

        const presentGroupIndividuals = groupIndividualData.filter((groupIndividual) => groupIndividual.attendanceStatus === 'present')

        if (presentGroupIndividuals.length === 0) {
            console.error('No present group athletes found')
            setStartWorkoutLoading(false)
            return
        }

        // Flag workouts as started on the backend

        var workoutIds = [urlWorkoutId]
        for (const groupIndividual of presentGroupIndividuals) {
            workoutIds.push(groupIndividual?.workout?.workoutId)
        }

        const workoutsStarted = await StartV5Workouts(coachUserId, workoutIds)

        if (workoutsStarted === false) {
            console.error('Failed to flag workout as started!!')
            setStartWorkoutLoading(false)
            return
        }

        // Initialize the active workout V5 slice

        const groupActiveWorkoutV5Payload = {
            kinesisWorkoutAssignmentType: kinesisWorkoutAssignmentType,
            workoutId: urlWorkoutId,
            coachUserId: coachUserId,
            kinesisUserGroupId: groupDetails.id,
            groupDetails: groupDetails,
            workout: workout,
            groupIndividuals: presentGroupIndividuals
        }

        dispatch(initializeActiveWorkoutV5(groupActiveWorkoutV5Payload))

        setStartWorkoutLoading(false)

        navigate(`/workout/${urlWorkoutId}/active`)
    }

    const __StartIndividualWorkout = async () => {

        if (urlWorkoutId !== workout.workoutId) {
            console.error('Workout ID mismatch!')
            return
        }

        setStartWorkoutLoading(true)

        // Flag workouts as started on the backend

        var workoutIds = [urlWorkoutId]

        const workoutsStarted = await StartV5Workouts(coachUserId, workoutIds)

        if (workoutsStarted === false) {
            console.error('Failed to flag workout as started!!')
            setStartWorkoutLoading(false)
            return
        }

        // Initialize the active workout V5 slice

        const individualActiveWorkoutV5Payload = {
            kinesisWorkoutAssignmentType: 'individual', // A group individual's copy runs as an individual workout
            workoutId: urlWorkoutId,
            coachUserId: coachUserId,
            userId: userDetails?.userId,
            userDetails: userDetails,
            workout: workout
        }

        dispatch(initializeActiveWorkoutV5(individualActiveWorkoutV5Payload))

        setStartWorkoutLoading(false)

        navigate(`/workout/${urlWorkoutId}/active`)
    }

    useEffect(() => { LoadWorkoutPreview() }, [urlWorkoutId])

    if (
        urlWorkoutId === null 
        || urlWorkoutId === undefined
        || exercises.length === 0 
        || Object.keys(exerciseHashMap).length === 0
        || workoutPreviewData === null
        || workout === null
        || (kinesisWorkoutAssignmentType === 'group' && groupIndividualData === null)
    ) {
        return (
            <ScreenFrame alwaysHideResumeWorkoutHeader={true}>
                <div className='space-y-4'>
                    <div className='w-full h-20 bg-gray-200 rounded-lg animate-pulse' />

                    <div className='grid grid-cols-2 gap-4'>
                        <div className='w-full h-20 bg-gray-200 rounded-lg animate-pulse' />
                        <div className='w-full h-20 bg-gray-200 rounded-lg animate-pulse' />
                    </div>

                    <div className='grid grid-cols-2 gap-4'>
                        <div className='w-full h-20 bg-gray-200 rounded-lg animate-pulse' />
                        <div className='w-full h-20 bg-gray-200 rounded-lg animate-pulse' />
                    </div>

                    <div className='grid grid-cols-2 gap-4'>
                        <div className='w-full h-20 bg-gray-200 rounded-lg animate-pulse' />
                        <div className='w-full h-20 bg-gray-200 rounded-lg animate-pulse' />
                    </div>
                </div>
            </ScreenFrame>
        )
    }

    return (
        <ScreenFrame alwaysHideResumeWorkoutHeader={true}>
            <div className="flex items-center mb-6 gap-x-4">
                <h1 className="flex-1 min-w-0">
                    <p className='text-3xl font-bold max-w-[55vw] 2xl:max-w-[50vw] truncate'>
                        {workout.name}
                    </p>
                </h1>

                <div className='w-40'>
                    <SolidButton
                        size="large"
                        text="Back"
                        action={() => navigate(-1)}
                        className='shadow-md'
                    />
                </div>

                <div className='w-40'>
                    <BrandColorButton
                        size="large"
                        text="Start"
                        action={() => OnStartWorkoutButton()}
                        isLoading={startWorkoutLoading}
                        className='shadow-md'
                    />
                </div>
            </div>

            <div className='flex gap-x-12'>
                <div className='flex-1 flex flex-col space-y-8'>
                    {kinesisWorkoutAssignmentType === 'group' && (
                        <GroupAttendance 
                            groupAthleteAttendanceData={groupIndividualData} 
                            setGroupAthleteAttendanceData={SetGroupIndividualDataInsideWorkoutPreviewDataObject}
                        />
                    )}

                    {isIndividualRun && (
                        <>
                            <p>Athlete</p>

                            <div className='flex items-center rounded-xl overflow-hidden text-center bg-white p-4 gap-x-4 shadow-md'>
                                <UserProfilePicture 
                                    firstName={userDetails.firstName} 
                                    lastName={userDetails.lastName} 
                                    shadow={false}
                                />

                                <p className='text-lg'>
                                    {userDetails.firstName} {userDetails.lastName}
                                </p>
                            </div>

                            <p>Equipment</p>

                            <WorkoutV5EquipmentPreview 
                                exerciseHashMap={exerciseHashMap}
                                workout={workout} 
                            />
                        </>
                    )}
                </div>

                <div className="flex-1 flex flex-col mb-8 space-y-4">
                    {kinesisWorkoutAssignmentType === 'group' && (
                        <>
                            <p>Equipment</p>

                            <WorkoutV5EquipmentPreview 
                                exerciseHashMap={exerciseHashMap}
                                workout={workout} 
                            />
                        </>
                    )}
                    
                    <p>Exercises</p>

                    <WorkoutV5PreviewComponent
                        exerciseHashMap={exerciseHashMap}
                        workoutPreviewData={workoutPreviewData}
                        setWorkoutPreviewData={setWorkoutPreviewData}
                    />
                </div>
            </div>
        </ScreenFrame>
    )
}